import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  PayoutsController_record_v1,
  type PayoutsController_record_v1Body,
  type TillPayoutRecordedResource,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { formatPaise, paiseFromRupees } from '@/lib/money';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import { fingerprintOfPayout, payoutSchema, type PayoutValues } from './day-close-form';

/**
 * Cash out of the drawer for something that is not a refund:
 * `POST /v1/outlets/{outletId}/till-payouts`.
 *
 * **The only way cash leaves a drawer without a refund**, and so the first thing
 * a variance investigation reads. It reduces expected cash and leaves rung-up
 * untouched (FR-625, FR-626): the day still took what it took, and there is
 * simply less of it in the drawer.
 *
 * ## The route takes no date, and the screen must say so
 *
 * A payout attaches to the outlet's **current** trading day, resolved by the
 * server (FR-622) — back-dating one would let somebody move cash between days
 * after the fact. So this dialog carries no date field however plainly the
 * screen behind it is looking at one, and the answer names the day it actually
 * landed in. If that is not the day on screen, the screen says so rather than
 * quietly showing a reconciliation the payout is not in.
 *
 * `BUSINESS_DAY_CLOSED` is what comes back when that current day is shut, and it
 * is deliberately **not retryable**: an owner reopening the day is a decision
 * somebody makes, not a delay to wait out.
 *
 * ## The id is the row's own, and the header is what identifies a retry
 *
 * `RecordTillPayoutDto.id` is client-minted and is "the row's own identifier …
 * it is not what identifies a retry — the `Idempotency-Key` header is". Both are
 * minted together by `attemptFor` and both are held for as long as the body
 * stands, which is what makes a replay byte-identical: an id minted separately
 * would make every retry a different body and the replay would never happen.
 * Sending an id already recorded is `400 VALIDATION_FAILED` and **no replay of
 * it can succeed**, which is the failure that pattern exists to prevent.
 *
 * A mistaken payout is corrected by a second, offsetting one carrying its own
 * reason (FR-624). It is never edited — `till_payouts` refuses that by trigger —
 * and the copy says so, because somebody who thinks this is editable will write
 * a thinner reason.
 */
export function RecordPayoutDialog({
  open,
  onOpenChange,
  outletId,
  outletName,
  onRecorded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  outletId: string;
  outletName: string;
  onRecorded: (payout: TillPayoutRecordedResource) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>Record a till payout</DialogTitle>
          <DialogDescription>
            {outletName}. Cash leaving the drawer for something that is not a refund. It lands in
            this outlet&rsquo;s current trading day, which the server decides — there is no date to
            choose here, so cash cannot be moved between days afterwards.
          </DialogDescription>
        </DialogHeader>

        {/* A child, so closing the dialog unmounts it and the next payout starts
            from an empty form rather than from the last one's figures. */}
        <RecordPayoutForm outletId={outletId} onRecorded={onRecorded} />
      </FormDialogContent>
    </Dialog>
  );
}

function RecordPayoutForm({
  outletId,
  onRecorded,
}: {
  outletId: string;
  onRecorded: (payout: TillPayoutRecordedResource) => void;
}) {
  const client = useApiClient();

  const form = useForm<PayoutValues>({
    resolver: zodResolver(payoutSchema),
    mode: 'onTouched',
    defaultValues: { amount: '', reason: '' },
  });

  const [formError, setFormError] = useState<string | null>(null);

  /** The attempt this dialog is currently under. Nothing renders from it. */
  const attempt = useRef<WriteAttempt | null>(null);

  const pending = form.formState.isSubmitting;

  const handleSubmit = async (values: PayoutValues) => {
    if (pending) return;

    setFormError(null);

    // Everything but the id, which is minted with the key below because the two
    // together are what makes one attempt one write.
    const content = {
      // Integer paise, moved as digits and never by multiplying a decimal. A
      // positive magnitude: the direction is implicit in the act, and the schema
      // has already refused a minus sign and a zero.
      amountPaise: paiseFromRupees(values.amount),

      // Sent exactly as typed — stored as submitted, returned as data, and never
      // editable afterwards.
      reason: values.reason,
    };

    const sending = attemptFor(attempt.current, fingerprintOfPayout(outletId, content));

    attempt.current = sending;

    try {
      // The body's shape comes from the generated client. There is deliberately
      // no outlet, no actor, no instant and no business date to carry — the
      // outlet is in the path and the rest are the server's.
      const body: PayoutsController_record_v1Body = { id: sending.id, ...content };

      onRecorded(
        await client.request(PayoutsController_record_v1, {
          path: { outletId },
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
        }),
      );
    } catch (error: unknown) {
      if (error instanceof ApiError && error.code === 'DAY_CLOSE_PAYOUT_AMOUNT_INVALID') {
        form.setError('amount', { message: messageForFailure(error) });
        form.setFocus('amount');

        return;
      }

      if (error instanceof ApiError && error.code === 'DAY_CLOSE_PAYOUT_REASON_INVALID') {
        form.setError('reason', { message: messageForFailure(error) });
        form.setFocus('reason');

        return;
      }

      // `BUSINESS_DAY_CLOSED` lands here — the outlet's current day is shut and
      // an owner has to reopen it before anything can be recorded against it.
      // So does `RESOURCE_NOT_FOUND`, which covers an unknown outlet, a retired
      // one and one out of reach indistinguishably.
      setFormError(messageForFailure(error));
    }
  };

  const typed = form.watch('amount').trim();

  const previewPaise = /^\d+(\.\d{1,2})?$/.test(typed) ? paiseFromRupees(typed) : null;

  return (
    <Form {...form}>
      <form
        className="space-y-5"
        noValidate
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
      >
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                How much left the drawer
              </FormLabel>
              <div className="relative">
                <span
                  aria-hidden
                  className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-muted-foreground"
                >
                  ₹
                </span>
                <FormControl
                  render={
                    <Input
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder="800.00"
                      className={`${fieldSurface} h-11 w-full pl-8 font-mono numeric`}
                      required
                      {...field}
                    />
                  }
                />
              </div>
              <FormDescription>
                Rupees and paise, with no ₹ and no commas. Cash leaving is what a payout means, so
                there is no minus sign to add.
                {previewPaise !== null && (
                  <>
                    {' '}
                    Recorded as{' '}
                    <span className="font-mono numeric">{formatPaise(previewPaise)}</span>.
                  </>
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                What the cash was for
              </FormLabel>
              <FormControl
                render={
                  <Input
                    className={`${fieldSurface} h-11 w-full`}
                    autoComplete="off"
                    placeholder="Vegetables, paid to supplier"
                    required
                    {...field}
                  />
                }
              />
              <FormDescription>
                Cash out of the drawer with nothing said about it cannot be traced, and this is the
                line somebody reads first when a day comes up short. It cannot be edited afterwards
                — a mistake is put right by a second payout that says so.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {formError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="submit" disabled={pending}>
            {pending ? 'Recording…' : 'Record the payout'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
